import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

// Validate environment variables
const validateEnvVars = () => {
  const required = [
    "VITE_AWS_REGION",
    "VITE_AWS_ACCESS_KEY_ID",
    "VITE_AWS_SECRET_ACCESS_KEY",
    "VITE_S3_BUCKET_NAME",
  ];

  const missing = required.filter((key) => !import.meta.env[key]);
  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(", ")}`
    );
  }
};

// Validate on module load
validateEnvVars();

const s3Client = new S3Client({
  region: import.meta.env.VITE_AWS_REGION,
  credentials: {
    accessKeyId: import.meta.env.VITE_AWS_ACCESS_KEY_ID,
    secretAccessKey: import.meta.env.VITE_AWS_SECRET_ACCESS_KEY,
  },
});

const BUCKET_NAME = import.meta.env.VITE_S3_BUCKET_NAME;
const UPLOAD_FOLDER = "uploads/";
// Get expiry time from env (default: 600 seconds = 10 minutes)
const PRESIGNED_URL_EXPIRY = parseInt(
  import.meta.env.VITE_PRESIGNED_URL_EXPIRY || "600",
  10
);

interface PresignedUrlResponse {
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
}

/**
 * Validates file type and size before generating presigned URL
 * @param file - File object to validate
 * @param maxSizeMB - Maximum file size in MB (default: 22MB)
 */
export function validateFile(
  file: File,
  maxSizeMB: number = 22
): { valid: boolean; error?: string } {
  // Allowed file types
  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: "Invalid file type. Please upload a PDF, JPEG, JPG, or PNG file.",
    };
  }

  // Check file size (convert MB to bytes)
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File size must be less than ${maxSizeMB}MB.`,
    };
  }

  return { valid: true };
}

/**
 * Generates a presigned URL for uploading a file to S3
 * @param fileName - Original file name
 * @param contentType - MIME type (default: application/pdf)
 * @param expiresIn - URL expiration time in seconds (default: from env or 600 = 10 minutes)
 * @returns Presigned URL response with uploadUrl, fileKey, and fileUrl
 */
export async function getPresignedUploadUrl(
  fileName: string,
  contentType: string = "application/pdf",
  expiresIn?: number
): Promise<PresignedUrlResponse> {
  // Use env variable expiry or provided expiry (default: 600 seconds = 10 minutes)
  const expiryTime = expiresIn || PRESIGNED_URL_EXPIRY;

  // Validate expiry time (max 1 hour for security)
  if (expiryTime > 3600) {
    throw new Error("Presigned URL expiry cannot exceed 3600 seconds (1 hour)");
  }

  // Generate unique file key with timestamp and random string for security
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, "_");
  const fileKey = `${UPLOAD_FOLDER}${timestamp}_${randomString}_${sanitizedFileName}`;

  const command = new PutObjectCommand({
    Bucket: BUCKET_NAME,
    Key: fileKey,
    ContentType: contentType,
    // Add metadata for additional security
    Metadata: {
      uploadedAt: new Date().toISOString(),
      originalFileName: fileName,
    },
  });

  const uploadUrl = await getSignedUrl(s3Client, command, {
    expiresIn: expiryTime,
  });

  // Construct the final file URL (after upload)
  const fileUrl = `https://${BUCKET_NAME}.s3.${
    import.meta.env.VITE_AWS_REGION
  }.amazonaws.com/${fileKey}`;

  return {
    uploadUrl,
    fileKey,
    fileUrl,
  };
}

/**
 * Uploads a file to S3 using a presigned URL
 * @param file - File object to upload
 * @param onProgress - Optional progress callback
 * @param expiresIn - Optional custom expiry time (default: from env)
 * @returns Object with fileKey and fileUrl
 */
export async function uploadFileToS3(
  file: File,
  onProgress?: (progress: number) => void,
  expiresIn?: number
): Promise<{ fileKey: string; fileUrl: string }> {
  // Validate file before upload
  const validation = validateFile(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Generate presigned URL
  const { uploadUrl, fileKey, fileUrl } = await getPresignedUploadUrl(
    file.name,
    file.type || "application/pdf",
    expiresIn
  );

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Security: Only allow PUT method
    xhr.open("PUT", uploadUrl);

    // Set required headers
    xhr.setRequestHeader("Content-Type", file.type || "application/pdf");

    // Track upload progress
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    // Handle successful upload
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ fileKey, fileUrl });
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    // Handle errors
    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed due to network error"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload was aborted"));
    });

    // Send file
    xhr.send(file);
  });
}
